import { LAYOUT_MODES, SESSION_SCHEMA_VERSION, ZOOM_LEVELS } from "../constants/previewConfig";
import { normalizeUrl } from "./urlUtils";

function generateId() {
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

export function isValidZoom(value) {
  return value === "fit" || (typeof value === "number" && ZOOM_LEVELS.includes(value));
}

export function isValidLayoutMode(value) {
  return Object.values(LAYOUT_MODES).includes(value);
}

/**
 * Coerces a stored/imported zoom value to one the UI can actually render:
 * "fit" or one of ZOOM_LEVELS (numeric strings like "0.5" are accepted).
 * Anything else falls back to "fit" so the readout can never show "NaN%".
 */
export function sanitizeZoom(value) {
  if (isValidZoom(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const numeric = Number(value);
    if (ZOOM_LEVELS.includes(numeric)) return numeric;
  }
  return "fit";
}

export function sanitizeLayoutMode(value) {
  return isValidLayoutMode(value) ? value : LAYOUT_MODES.GRID;
}

/** Keeps only `{ [deviceId]: boolean }` entries; drops anything else. */
export function sanitizeOrientationMap(value) {
  if (!isPlainObject(value)) return {};
  return Object.entries(value).reduce((map, [id, rotated]) => {
    if (typeof rotated === "boolean") map[id] = rotated;
    return map;
  }, {});
}

export function createSession({
  name,
  url,
  selectedDeviceIds,
  orientationByDevice,
  globalZoom,
  layoutMode,
}) {
  const now = new Date().toISOString();
  return {
    id: generateId(),
    schemaVersion: SESSION_SCHEMA_VERSION,
    name: name?.trim() || "Untitled session",
    url,
    selectedDeviceIds: [...selectedDeviceIds],
    orientationByDevice: { ...orientationByDevice },
    globalZoom,
    layoutMode,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Validates that an arbitrary parsed value looks like a session, so
 * imported/localStorage JSON can never crash the app, corrupt state, or
 * smuggle in an unsafe URL. Returns a cleaned copy, or null when invalid.
 *
 * The URL goes through the same normalizeUrl() check as the address bar, so
 * only http(s) URLs with a real host survive — `javascript:`, `data:` etc.
 * are rejected outright rather than ever reaching an iframe `src`.
 */
export function validateSessionShape(value) {
  if (!isPlainObject(value)) return null;
  if (typeof value.name !== "string") return null;
  if (typeof value.url !== "string") return null;
  if (!Array.isArray(value.selectedDeviceIds)) return null;
  if (!value.selectedDeviceIds.every((id) => typeof id === "string")) return null;
  if (value.orientationByDevice !== undefined && !isPlainObject(value.orientationByDevice)) {
    return null;
  }

  const urlResult = normalizeUrl(value.url);
  if (!urlResult.valid) return null;

  const now = new Date().toISOString();
  return {
    id: typeof value.id === "string" && value.id ? value.id : generateId(),
    schemaVersion: SESSION_SCHEMA_VERSION,
    name: value.name.trim() || "Untitled session",
    url: urlResult.url,
    selectedDeviceIds: [...value.selectedDeviceIds],
    orientationByDevice: sanitizeOrientationMap(value.orientationByDevice),
    globalZoom: sanitizeZoom(value.globalZoom),
    layoutMode: sanitizeLayoutMode(value.layoutMode),
    createdAt: typeof value.createdAt === "string" ? value.createdAt : now,
    updatedAt: typeof value.updatedAt === "string" ? value.updatedAt : now,
  };
}

/**
 * Cleans a stored sessions list: drops anything that isn't a valid session
 * and re-keys duplicate ids (which older versions could create on import),
 * so React keys stay unique and rename/delete only touch one session.
 * Always returns an array.
 */
export function validateSessionsList(value) {
  if (!Array.isArray(value)) return [];
  const seenIds = new Set();
  return value
    .map(validateSessionShape)
    .filter(Boolean)
    .map((session) => {
      const unique = seenIds.has(session.id) ? { ...session, id: generateId() } : session;
      seenIds.add(unique.id);
      return unique;
    });
}

export function parseImportedSessionJson(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { valid: false, session: null, error: "That file isn't valid JSON." };
  }

  const session = validateSessionShape(parsed);
  if (!session) {
    return { valid: false, session: null, error: "That file doesn't match the session format." };
  }

  // Always mint a fresh id: an exported file carries its original id, and
  // re-importing it next to the original would otherwise duplicate it.
  const now = new Date().toISOString();
  return { valid: true, session: { ...session, id: generateId(), updatedAt: now }, error: null };
}

export function serializeSession(session) {
  return JSON.stringify(session, null, 2);
}
